import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { FormlyMaterialModule } from '@ngx-formly/material';
import { FormlyMatDatepickerModule } from '@ngx-formly/material/datepicker';
import { multipleEmailsValidator, slugValidator, urlValidator } from '@shared/components/data-form/validators/multiple-emails-validator';
import { AbstractControl } from '@angular/forms';
import { BankAccountComponent } from '@shared/components/data-form/bank-account/bank-account.component';
import { CreditCardComponent } from '@shared/components/data-form/credit-card/credit-card.component';
import { FileUploaderComponent } from '@shared/components/data-form/file-uploader/file-uploader.component';
import { MapLocationComponent } from '@shared/components/data-form/map-location/map-location.component';
import { QuestionFormComponent } from '@shared/components/data-form/question/question-form.component';
import { SignatureComponent } from '@shared/components/data-form/signature/signature.component';
import { AutocompleteTypeComponent } from '@shared/components/data-form/types/autocomplete-type.component';
import { AutocompleteUsersTypeComponent } from '@shared/components/data-form/types/autocomplete-users-type.component';
import { ButtonTypeComponent } from '@shared/components/data-form/types/button-type.component';
import { ColorPickerCustomComponent } from '@shared/components/data-form/types/color-picker.type';
import { CurrencyInputFieldTypeComponent } from '@shared/components/data-form/types/currency-type.component';
import { DatepickerTypeComponent } from '@shared/components/data-form/types/datepicker-type.component';
import { MultiSelectTypeComponent } from '@shared/components/data-form/types/multi-select-type.component';
import { InnerHtmlComponent } from '@shared/components/data-form/types/inner-html.component';
import { MaskInputComponent } from '@shared/components/data-form/types/mask-input.component';
import { PasswordInputComponent } from '@shared/components/data-form/types/password-input.component';
import { FieldQuillType } from '@shared/components/data-form/types/quill.type';
import { SelectActionTypeComponent } from '@shared/components/data-form/types/select-action-type.component';
import { SidemarkPricingConfigTypeComponent } from '@shared/components/data-form/types/sidemark-pricing-config.type';
import { FormlyStepError, PasswordMatchValue } from '@shared/models/domain/custom-views.model';

export const minlengthValidationMessages = (err, field: FormlyFieldConfig) => {
    return `Should have atleast ${field.props.minLength} characters`;
}

export const maxlengthValidationMessages = (err, field: FormlyFieldConfig) => {
    return `Should have at most ${field.props.maxLength} characters`;
}

export const stepValidationMessage =(err: FormlyStepError) => {
    return err.step === 1 && Number.isInteger(err.base)
        ? 'Please enter a whole number'
        : `Please enter a value in steps of ${err.step}`;
}

export const fieldMatchValidator = (control: AbstractControl) => {
    const { password, passwordConfirm } = control?.value as PasswordMatchValue || '' as PasswordMatchValue;
    if (!passwordConfirm || !password) {
        return null;
    }
    if (passwordConfirm === password) {
        return null;
    }
    return { fieldMatch: { message: 'Password Not Matching' } };
}

export const uniqueNameValidator = (control: AbstractControl, field: FormlyFieldConfig) => {
    const value = (control?.value as string)?.toLowerCase().trim();
    if (!value) return null;
    const existingNames = field?.props?.existingNames as string[] || [];
    return (value && existingNames.includes(value)) ? { uniqueName: true } : null;
}

export const createFormlyProviders = () => {
    return [
        FormlyModule.forRoot({
            extras: { lazyRender: true },
            validators: [
                { name: 'fieldMatch', validation: fieldMatchValidator },
                { name: 'multipleEmails', validation: multipleEmailsValidator },
                { name: 'url', validation: urlValidator },
                { name: 'slug', validation: slugValidator },
                { name: 'uniqueName', validation: uniqueNameValidator },
            ],
            types: [
                {
                    name: 'quill',
                    component: FieldQuillType,
                },
                {
                    name: 'datepicker',
                    component: DatepickerTypeComponent,
                    defaultOptions: {
                        props: { datepickerOptions: {} },
                    },
                },
                { name: 'autocomplete', component: AutocompleteTypeComponent },
                { name: 'autocomplete-users', component: AutocompleteUsersTypeComponent },
                { name: 'select-action', component: SelectActionTypeComponent },
                { name: 'multi-select', component: MultiSelectTypeComponent },
                { name: 'masked-input', component: MaskInputComponent },
                { name: 'password', component: PasswordInputComponent },
                { name: 'string', extends: 'input' },
                {
                    name: 'number',
                    extends: 'input',
                    defaultOptions: {
                        props: {
                            type: 'number',
                        },
                    },
                },
                {
                    name: 'integer',
                    extends: 'input',
                    defaultOptions: {
                        props: {
                            type: 'number',
                            step: 1,
                        },
                    },
                },
                { name: 'boolean', extends: 'checkbox' },
                {
                    name: 'select',
                    defaultOptions: {
                        props: { panelWidth: 'auto' },
                    },
                },
                { name: 'enum', extends: 'select' },
                {
                    name: 'maplocation',
                    component: MapLocationComponent,
                },
                { name: 'colorpicker', component: ColorPickerCustomComponent },
                { name: 'signature-pad', component: SignatureComponent },
                { name: 'button', component: ButtonTypeComponent },
                { name: 'innerHTML', component: InnerHtmlComponent },
                {
                    name: 'currency',
                    component: CurrencyInputFieldTypeComponent,
                },
                {
                    name: 'uploadFile',
                    component: FileUploaderComponent,
                },
                {
                    name: 'creditCard',
                    component: CreditCardComponent,
                },
                {
                    name: 'bankAccount',
                    component: BankAccountComponent,
                },
                {
                    name: 'question',
                    component: QuestionFormComponent
                },
                {
                    name: 'sidemark-pricing-config',
                    component: SidemarkPricingConfigTypeComponent,
                }
            ],
            validationMessages: [
                { name: 'minLength', message: minlengthValidationMessages },
                { name: 'maxLength', message: maxlengthValidationMessages },
                { name: 'step', message: stepValidationMessage },
                {
                    name: 'multipleEmails',
                    message:
                        'Please enter valid email addresses separated by commas.',
                },
                { name: 'url', message: 'Please enter valid URL' },
                {
                    name: 'slug',
                    message: (err, field) =>
                        `You don't allow ${field.formControl.value} as a slug`,
                },
                {
                    name: 'uniqueName',
                    message: 'A room with this name already exists'
                },
            ],
        }),
        FormlyMaterialModule,
        FormlyMatDatepickerModule,
    ];
};
